<template>
    <AppLayout>
        <div class="container-fluid">
            <div class="widget">
                <div class="widget-header">
                    <h3>Statystyki zamówień</h3>
                </div>

                <div class="widget-content">
                    <div class="row mb-4">
                        <div class="col-md-2">
                            <label class="form-label">Rok</label>
                            <select
                                v-model="selectedYear"
                                class="form-select"
                                @change="changeWeek"
                                :disabled="loading"
                            >
                                <option
                                    v-for="year in years"
                                    :key="year"
                                    :value="year"
                                >
                                    {{ year }}
                                </option>
                            </select>
                        </div>

                        <div class="col-md-4">
                            <label class="form-label">Tydzień</label>
                            <select
                                v-model="selectedWeek"
                                class="form-select"
                                @change="changeWeek"
                                :disabled="loading"
                            >
                                <option
                                    v-for="week in weeks"
                                    :key="week.week"
                                    :value="week.week"
                                >
                                    Tydzień {{ week.week }}:
                                    {{ week.start }} - {{ week.end }}
                                </option>
                            </select>
                        </div>

                        <div class="col-md-6 d-flex align-items-end">
                            <div
                                v-if="weekData.start && weekData.end"
                                class="text-muted"
                            >
                                Wybrany okres:
                                <strong>
                                    {{ formatDate(weekData.start) }}
                                    -
                                    {{ formatDate(weekData.end) }}
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div class="custom-table">
                        <table class="table table-hover mb-0">
                            <thead>
                                <tr>
                                    <th width="50">#</th>
                                    <th>Użytkownik</th>
                                    <th width="150">Liczba zamówień</th>
                                    <th width="180">Suma netto</th>
                                    <th width="180">Suma rabatów</th>
                                </tr>
                            </thead>

                            <tbody>
                                <template
                                    v-for="(user, userIndex) in statisticsData"
                                    :key="user.user_id"
                                >
                                    <tr
                                        class="user-row"
                                        @click="toggleUser(user.user_id)"
                                    >
                                        <td>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-secondary expand-btn"
                                                @click.stop="toggleUser(user.user_id)"
                                            >
                                                {{ isUserOpen(user.user_id) ? '-' : '+' }}
                                            </button>
                                        </td>

                                        <td>
                                            <strong>
                                                {{ getUserName(user) }}
                                            </strong>
                                        </td>

                                        <td>
                                            {{ user.orders_count }}
                                        </td>

                                        <td>
                                            <strong>
                                                {{ formatMoney(getUserNetTotal(user)) }}
                                            </strong>
                                        </td>

                                        <td>
                                            <strong>
                                                {{ formatMoney(getUserDiscountTotal(user)) }}
                                            </strong>
                                        </td>
                                    </tr>

                                    <tr
                                        v-if="isUserOpen(user.user_id)"
                                        :key="'user-' + user.user_id"
                                    >
                                        <td colspan="5" class="nested-table">
                                            <table class="table table-bordered table-sm mb-0">
                                                <thead>
                                                    <tr>
                                                        <th width="50">#</th>
                                                        <th>ID zamówienia</th>
                                                        <th>Data</th>
                                                        <th>Waluta</th>
                                                        <th>Netto</th>
                                                        <th>Rabat</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    <template
                                                        v-for="(order, orderIndex) in user.orders"
                                                        :key="order.id"
                                                    >
                                                        <tr
                                                            class="order-row"
                                                            @click="toggleOrder(order.id)"
                                                        >
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-sm btn-outline-secondary expand-btn"
                                                                    @click.stop="toggleOrder(order.id)"
                                                                >
                                                                    {{ isOrderOpen(order.id) ? '-' : '+' }}
                                                                </button>
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {{ order.number }}
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                {{ formatDateTime(order.created_at) }}
                                                            </td>

                                                            <td>
                                                                {{ getOrderCurrency(order) }}
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {{ formatMoney(getOrderNetTotal(order)) }}
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {{ formatMoney(getOrderDiscountTotal(order)) }}
                                                                </strong>
                                                            </td>
                                                        </tr>

                                                        <tr
                                                            v-if="isOrderOpen(order.id)"
                                                            :key="'order-' + order.id"
                                                        >
                                                            <td
                                                                colspan="6"
                                                                class="items-table"
                                                            >
                                                                <table class="table table-bordered table-sm mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Artykuł</th>
                                                                            <th>Model</th>
                                                                            <th>Producent</th>
                                                                            <th width="100">Ilość</th>
                                                                            <th width="130">Cena netto</th>
                                                                            <th width="100">Rabat %</th>
                                                                            <th width="140">Wartość rabatu</th>
                                                                            <th width="140">Netto po rabacie</th>
                                                                            <th width="100">Waluta</th>
                                                                            <th width="140">Wartość netto</th>
                                                                        </tr>
                                                                    </thead>

                                                                    <tbody>
                                                                        <tr
                                                                            v-for="item in order.items"
                                                                            :key="item.id"
                                                                        >
                                                                            <td>
                                                                                {{ getArticleName(item) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ getArticleModel(item) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ getProducer(item) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ getItemQuantity(item) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ formatMoney(item.price_netto) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ formatDiscount(item.rabate) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ formatMoney(getItemDiscountValue(item)) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ formatMoney(getItemNetAfterDiscount(item)) }}
                                                                            </td>

                                                                            <td>
                                                                                {{ item.currency || '-' }}
                                                                            </td>

                                                                            <td>
                                                                                <strong>
                                                                                    {{ formatMoney(item.value_netto) }}
                                                                                </strong>
                                                                            </td>
                                                                        </tr>

                                                                        <tr v-if="!order.items || !order.items.length">
                                                                            <td
                                                                                colspan="10"
                                                                                class="text-center text-muted"
                                                                            >
                                                                                Brak pozycji
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    </template>

                                                    <tr v-if="!user.orders || !user.orders.length">
                                                        <td
                                                            colspan="6"
                                                            class="text-center text-muted"
                                                        >
                                                            Brak zamówień
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                </template>

                                <tr v-if="!statisticsData.length">
                                    <td
                                        colspan="5"
                                        class="text-center text-muted py-4"
                                    >
                                        Brak zamówień w wybranym tygodniu
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="loading" class="loading-overlay">
            <div class="loading-modal">
                <div class="spinner-border text-primary loading-spinner" role="status">
                    <span class="visually-hidden">Ładowanie...</span>
                </div>

                <div class="loading-title">
                    Ładowanie danych
                </div>

                <div class="loading-text">
                    Proszę czekać, pobieranie statystyk może potrwać chwilę...
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/App.vue";

export default {
    components: {
        AppLayout,
    },

    props: {
        statistics: {
            type: Array,
            default: () => [],
        },

        week: {
            type: Object,
            default: () => ({
                start: null,
                end: null,
            }),
        },
    },

    data() {
        const now = new Date();

        return {
            statisticsData: [...this.statistics],
            weekData: { ...this.week },
            openedUsers: [],
            openedOrders: [],
            selectedYear: now.getFullYear(),
            selectedWeek: this.getWeekNumber(now),
            loading: false,
        };
    },

    computed: {
        years() {
            const currentYear = new Date().getFullYear();

            return Array.from(
                { length: 5 },
                (_, index) => currentYear - index
            );
        },

        weeks() {
            const year = Number(this.selectedYear);
            const currentYear = new Date().getFullYear();
            const currentWeek = this.getWeekNumber(new Date());
            const count = this.getISOWeeksInYear(year);

            const maxWeek = year === currentYear
                ? currentWeek
                : year < currentYear
                    ? count
                    : 0;

            return Array.from(
                { length: maxWeek },
                (_, index) => {
                    const week = index + 1;
                    const start = this.getWeekStart(year, week);
                    const end = new Date(start);

                    end.setDate(end.getDate() + 6);

                    return {
                        week,
                        start: this.formatDateShort(start),
                        end: this.formatDateShort(end),
                    };
                }
            ).reverse();
        },
    },

    methods: {
        getWeekNumber(date) {
            const d = new Date(
                Date.UTC(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate()
                )
            );

            const dayNum = d.getUTCDay() || 7;

            d.setUTCDate(d.getUTCDate() + 4 - dayNum);

            const yearStart = new Date(
                Date.UTC(
                    d.getUTCFullYear(),
                    0,
                    1
                )
            );

            return Math.ceil(
                (((d - yearStart) / 86400000) + 1) / 7
            );
        },

        getISOWeeksInYear(year) {
            const dec28 = new Date(year, 11, 28);

            return this.getWeekNumber(dec28);
        },

        getWeekStart(year, week) {
            const january4 = new Date(year, 0, 4);
            const day = january4.getDay() || 7;
            const monday = new Date(january4);

            monday.setDate(
                january4.getDate() -
                day +
                1 +
                ((week - 1) * 7)
            );

            return monday;
        },

        formatDateShort(date) {
            return date.toLocaleDateString("pl-PL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        },

        async changeWeek() {
            const year = Number(this.selectedYear);
            const maxWeek = this.getISOWeeksInYear(year);

            if (Number(this.selectedWeek) > maxWeek) {
                this.selectedWeek = maxWeek;
            }

            this.loading = true;

            try {
                const response = await axios.get(
                    route("orderLists.stats.week"),
                    {
                        params: {
                            year: Number(this.selectedYear),
                            week: Number(this.selectedWeek),
                        },
                    }
                );

                this.statisticsData = response.data.statistics || [];

                this.weekData = response.data.week || {
                    start: null,
                    end: null,
                };

                this.openedUsers = [];
                this.openedOrders = [];
            } catch (error) {
                console.error(error);

                Toast.fire({
                    toast: true,
                    icon: "error",
                    title: "Nie udało się załadować danych.",
                    position: "top-end",
                    timer: 2500,
                    showConfirmButton: false,
                });
            } finally {
                this.loading = false;
            }
        },

        formatDateForApi(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");

            return `${year}-${month}-${day}`;
        },

        toggleUser(userId) {
            const index = this.openedUsers.indexOf(userId);

            if (index === -1) {
                this.openedUsers.push(userId);
            } else {
                this.openedUsers.splice(index, 1);
            }
        },

        toggleOrder(orderId) {
            const index = this.openedOrders.indexOf(orderId);

            if (index === -1) {
                this.openedOrders.push(orderId);
            } else {
                this.openedOrders.splice(index, 1);
            }
        },

        isUserOpen(userId) {
            return this.openedUsers.includes(userId);
        },

        isOrderOpen(orderId) {
            return this.openedOrders.includes(orderId);
        },

        getUserName(user) {
            return user.user_name || `${user.user_id}`;
        },

        getUserNetTotal(user) {
            return (user.orders || []).reduce(
                (total, order) => total + this.getOrderNetTotal(order),
                0
            );
        },

        getUserDiscountTotal(user) {
            return (user.orders || []).reduce(
                (total, order) => total + this.getOrderDiscountTotal(order),
                0
            );
        },

        getOrderNetTotal(order) {
            return (order.items || []).reduce(
                (total, item) => total + Number(item.value_netto || 0),
                0
            );
        },

        getOrderDiscountTotal(order) {
            return (order.items || []).reduce(
                (total, item) => total + this.getItemDiscountValue(item),
                0
            );
        },

        getOrderCurrency(order) {
            const currencies = [
                ...(order.items || [])
                    .map(item => item.currency)
                    .filter(Boolean),
            ];

            return [...new Set(currencies)].join(", ") || "-";
        },

        getArticleName(item) {
            return item.article?.catalog_number;
        },

        getArticleModel(item) {
            return (
                item.article?.model ||
                item.article?.description
            );
        },

        getProducer(item) {
            return item.article?.producer?.name || "-";
        },

        getItemQuantity(item) {
            return Number(item.quantity_required || 0);
        },

        getItemDiscountValue(item) {
            const valueNetto = Number(item.value_netto || 0);
            const rabate = Number(item.rabate || 0);

            return valueNetto * rabate / 100;
        },

        getItemNetAfterDiscount(item) {
            const valueNetto = Number(item.value_netto || 0);

            return valueNetto - this.getItemDiscountValue(item);
        },

        formatDiscount(value) {
            const discount = Number(value || 0);

            return `${discount.toFixed(2)}%`;
        },

        formatMoney(value) {
            return Number(value || 0).toLocaleString("pl-PL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },

        formatDate(value) {
            if (!value) {
                return "-";
            }

            return new Date(value).toLocaleDateString("pl-PL");
        },

        formatDateTime(value) {
            if (!value) {
                return "-";
            }

            return new Date(value).toLocaleString("pl-PL");
        },
    },
};
</script>

<style scoped>
.user-row {
    cursor: pointer;
}

.order-row {
    cursor: pointer;
}

.user-row:hover,
.order-row:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.expand-btn {
    min-width: 28px;
    width: 28px;
    height: 28px;
    padding: 0;
    line-height: 1;
}

.nested-table {
    padding: 10px 20px 10px 50px;
    background: #f8f9fa;
}

.items-table {
    padding: 10px 20px 10px 50px;
    background: #f1f3f5;
}

.items-table table {
    background: #fff;
}

.items-table th,
.items-table td {
    vertical-align: middle;
    white-space: nowrap;
}

.loading-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-modal {
    width: 320px;
    max-width: calc(100vw - 40px);
    padding: 30px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    text-align: center;
}

.loading-spinner {
    width: 42px;
    height: 42px;
    margin-bottom: 18px;
}

.loading-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.loading-text {
    color: #6c757d;
    font-size: 14px;
    line-height: 1.5;
}
</style>